import styled from '@emotion/styled';
import { PropertyItem, SubPropertyItem } from '../types';
import { getKey } from '../utils';
import { InfoPopover } from './';

interface TableRendererProps {
    description: string;
    items: Array<PropertyItem>;
}

interface DivTableCellProps {
    isBold: boolean;
}

const KEYS = ['name', 'value'];

const DivTableContainer = styled.div`
    .server-message-response-content--table {
        width: 100%;
    }
    .server-message-response-content--table-row {
        display: grid;
        grid-template-columns: 1fr 2fr;
        grid-column-gap: 24px;
    }
    .server-message-response-content--cell {
        display: block;
        font-size: 12px;
        font-weight: normal;
        color: #595959;
        line-height: 18px;
    }
    @media only screen and (max-width: 500px) {
        .server-message-response-content--table-row {
            grid-template-columns: 1fr;
            grid-column-gap: 0;
        }
    }
`;

const DivTableCell = styled.div<DivTableCellProps>`
    padding: 8px;
    text-align: left;
    font-weight: ${({ isBold }) => {
        return isBold ? 700 : 400;
    }};
`;

const DivTableCaption = styled.div`
    margin: 24px 0;
    text-align: left;
    font-weight: 700;
`;

const renderCaption = (title: string): JSX.Element => {
    if (title) {
        return (
            <DivTableCaption data-cy='data-cy-product-data-title'>
                {title}
            </DivTableCaption>
        );
    }
    return null;
};

/**
 * TableRenderer component renders an HTML table using <div> elements.
 *
 * @returns {JSX.Element} The rendered table content component.
 * @component
 */
const TableRenderer = ({
    description: title,
    items: data,
}: TableRendererProps): JSX.Element => {
    if (data?.length) {
        const keys = KEYS;
        const renderRow = (row, rowIndex) => {
            const {
                subText,
                popOver,
            }: { subText: SubPropertyItem; popOver: SubPropertyItem } = row;
            return (
                <div
                    className='server-message-response-content--table-row'
                    key={getKey('row', rowIndex)}
                    data-cy={`data-cy-product-data-row`}
                >
                    {keys.map((key, cellIndex) => {
                        return (
                            <DivTableCell
                                key={getKey('cell', cellIndex)}
                                isBold={key === 'name'}
                                data-cy={`data-cy-product-data-row-${key === 'name' ? 'key' : 'value'}`}
                            >
                                {
                                    <InfoPopover
                                        target={
                                            key && key in row ? row[key] : ''
                                        }
                                        content={
                                            popOver && key in popOver
                                                ? popOver[key]
                                                : ''
                                        }
                                        subText={
                                            subText && key in subText
                                                ? subText[key]
                                                : ''
                                        }
                                    />
                                }
                            </DivTableCell>
                        );
                    })}
                </div>
            );
        };
        const renderRows = () => {
            return <>{data.map((row, rowIndex) => renderRow(row, rowIndex))}</>;
        };

        return (
            <DivTableContainer data-cy='data-cy-product-data'>
                {renderCaption(title)}
                <div className='server-message-response-content--table'>
                    {renderRows()}
                </div>
            </DivTableContainer>
        );
    }
    return null;
};

export default TableRenderer;
